import React, { useState, useMemo, useCallback, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:3001';

// Clientseitige Pagination — identisches Muster wie der Solver-Drilldown in
// TicketAnalysisPage.jsx (PAGE_SIZE, visibleCount, "Weitere X laden" + "Alle Y laden").
const PAGE_SIZE = 50;

const STATUS_LABELS = {
  new: 'Neu', open: 'Offen', pending: 'Wartend',
  solved: 'Gelöst', closed: 'Geschlossen',
};
const STATUS_COLORS = {
  new: 'status--new', open: 'status--open', pending: 'status--pending',
  solved: 'status--solved', closed: 'status--closed',
};
const KANAL_LABELS = {
  telefon: '📞 Telefon', mail: '✉ Mail',
  web: '🌐 Web', automatisierung: '⚙ Auto', unbekannt: '–',
};

const EVENT_CONFIG = {
  created:     { color: '#185FA5', bg: '#E6F1FB', label: 'Erstellt',      icon: '●' },
  status:      { color: '#854F0B', bg: '#FAEEDA', label: 'Status',        icon: '◆' },
  assignee_id: { color: '#3B6D11', bg: '#EAF3DE', label: 'Übernahme',     icon: '▲' },
  group_id:    { color: '#993556', bg: '#FBEAF0', label: 'Weiterleitung', icon: '→' },
};

function fmtDateTime(iso) {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('de-CH', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function TicketTimeline({ ticket, auditData, loading }) {
  if (loading) return (
    <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--color-text-secondary)' }}>
      Verlauf wird geladen…
    </div>
  );

  const history = auditData?.history || [];
  const events = [
    ticket.created_at ? { type: 'created', timestamp: ticket.created_at, kanal: ticket.kanal } : null,
    ...history.map(h => ({ ...h, type: h.field })),
  ].filter(Boolean).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const groupEvents = history.filter(h => h.field === 'group_id');
  const routingPath = [];
  if (groupEvents.length > 0) {
    if (groupEvents[0].fromGroup) routingPath.push({ name: groupEvents[0].fromGroup, isCurrent: false });
    groupEvents.forEach((ev, i) => {
      if (ev.toGroup) routingPath.push({ name: ev.toGroup, isCurrent: i === groupEvents.length - 1 });
    });
  }

  return (
    <div style={{ padding: '12px 16px', background: 'var(--color-background-secondary)', borderBottom: '1px solid var(--color-border-tertiary)' }}>
      {routingPath.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Routing-Pfad</div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
            {routingPath.map((step, i) => (
              <React.Fragment key={i}>
                <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: step.isCurrent ? '#EAF3DE' : '#F1EFE8', color: step.isCurrent ? '#3B6D11' : '#5F5E5A', border: step.isCurrent ? '1px solid #97C459' : '0.5px solid rgba(0,0,0,0.15)' }}>{step.name}</span>
                {i < routingPath.length - 1 && <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 14, top: 8, bottom: 8, width: 1, background: 'rgba(0,0,0,0.08)' }} />
        {events.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', paddingLeft: 40 }}>Kein Verlauf verfügbar</div>}
        {events.map((ev, i) => {
          const cfg = EVENT_CONFIG[ev.type] || EVENT_CONFIG.status;
          return (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, position: 'relative' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: cfg.bg, color: cfg.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, zIndex: 1, border: `1px solid ${cfg.color}33` }}>{cfg.icon}</div>
              <div style={{ paddingTop: 4, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 500, padding: '1px 6px', borderRadius: 99, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{fmtDateTime(ev.timestamp)}</span>
                </div>
                <div style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
                  {ev.type === 'created' && <>Ticket erstellt <span style={{ color: 'var(--color-text-secondary)' }}>— {ev.kanal === 'telefon' ? 'Telefonanruf' : ev.kanal === 'mail' ? 'E-Mail' : ev.kanal === 'web' ? 'Web' : 'unbekannt'}</span></>}
                  {ev.type === 'status' && <>{ev.previousValue ? <><span style={{ color: 'var(--color-text-secondary)' }}>{STATUS_LABELS[ev.previousValue] || ev.previousValue}</span> → </> : ''}<strong style={{ fontWeight: 500 }}>{STATUS_LABELS[ev.value] || ev.value}</strong></>}
                  {ev.type === 'assignee_id' && <>Übernommen von <strong style={{ fontWeight: 500 }}>{ev.toAgent || '–'}</strong>{ev.fromAgent ? <span style={{ color: 'var(--color-text-secondary)' }}> (vorher: {ev.fromAgent})</span> : ''}</>}
                  {ev.type === 'group_id' && <>Weitergeleitet{ev.fromGroup ? <> von <strong style={{ fontWeight: 500 }}>{ev.fromGroup}</strong></> : ''} → <strong style={{ fontWeight: 500, color: '#993556' }}>{ev.toGroup || '–'}</strong></>}
                  {ev.byAgentName && ev.type !== 'assignee_id' && <span style={{ color: 'var(--color-text-secondary)', marginLeft: 6, fontSize: 11 }}>durch {ev.byAgentName}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AgentDetailTickets({ tickets, loading, hideProdukt, total }) {
  const [filterKanal, setFilterKanal] = useState('alle');
  const [filterStatus, setFilterStatus] = useState('alle');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(null);
  const [auditCache, setAuditCache] = useState({});
  const [auditLoading, setAuditLoading] = useState({});
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Neue Ticket-Liste (Agent/Zeitraum-Wechsel) → Pagination zurücksetzen
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [tickets]);

  const filtered = useMemo(() => {
    let result = [...(tickets || [])];
    if (filterKanal !== 'alle') result = result.filter(t => t.kanal === filterKanal);
    if (filterStatus !== 'alle') result = result.filter(t => t.status === filterStatus);
    result.sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [tickets, filterKanal, filterStatus, sortField, sortDir]);

  const visible = filtered.slice(0, visibleCount);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const handleToggle = useCallback(async (ticketId) => {
    if (expanded === ticketId) { setExpanded(null); return; }
    setExpanded(ticketId);
    if (!ticketId || auditCache[ticketId]) return;
    setAuditLoading(prev => ({ ...prev, [ticketId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/tickets/${ticketId}/timeline`, { credentials: 'include' });
      const data = await res.json();
      setAuditCache(prev => ({ ...prev, [ticketId]: data }));
    } catch {
      setAuditCache(prev => ({ ...prev, [ticketId]: { history: [] } }));
    } finally {
      setAuditLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  }, [expanded, auditCache]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="sort-icon sort-icon--inactive">↕</span>;
    return <span className="sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading) return <div className="tickets-loading">Tickets werden geladen…</div>;

  return (
    <div className="tickets-block">
      <div className="tickets-filters">
        <label>Kanal
          <select value={filterKanal} onChange={e => setFilterKanal(e.target.value)}>
            <option value="alle">Alle</option>
            <option value="telefon">Telefon</option>
            <option value="mail">Mail</option>
            <option value="web">Web</option>
            <option value="automatisierung">Automatisierung</option>
          </select>
        </label>
        <label>Status
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="alle">Alle</option>
            <option value="open">Offen</option>
            <option value="pending">Wartend</option>
            <option value="solved">Gelöst</option>
            <option value="closed">Geschlossen</option>
          </select>
        </label>
        <span className="tickets-count">
          {filtered.length}{total && total > filtered.length ? ` von ${total}` : ''} Ticket{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="tickets-empty">Keine Tickets gefunden.</div>
      ) : (
        <div className="tickets-table-wrapper">
          <table className="tickets-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')}>ID <SortIcon field="id" /></th>
                <th onClick={() => handleSort('created_at')}>Erstellt <SortIcon field="created_at" /></th>
                <th onClick={() => handleSort('ersteAktivitaet')}>Erste Aktivität <SortIcon field="ersteAktivitaet" /></th>
                <th onClick={() => handleSort('subject')}>Betreff <SortIcon field="subject" /></th>
                {!hideProdukt && <th onClick={() => handleSort('produkt')}>Produkt <SortIcon field="produkt" /></th>}
                <th onClick={() => handleSort('kanal')}>Kanal <SortIcon field="kanal" /></th>
                <th onClick={() => handleSort('status')}>Status <SortIcon field="status" /></th>
                <th onClick={() => handleSort('gruppeName')}>Abteilung <SortIcon field="gruppeName" /></th>
                <th onClick={() => handleSort('bearbeitungMin')}>Bearbeitungszeit <SortIcon field="bearbeitungMin" /></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(ticket => (
                <React.Fragment key={ticket.id}>
                  <tr
                    style={{ cursor: 'pointer', background: expanded === ticket.id ? 'var(--color-background-secondary)' : undefined }}
                    onClick={() => handleToggle(ticket.id)}
                  >
                    <td>
                      <a href={`https://supportsorba.zendesk.com/agent/tickets/${ticket.id}`} target="_blank" rel="noopener noreferrer" className="ticket-link" onClick={e => e.stopPropagation()}>#{ticket.id}</a>
                    </td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(ticket.created_at)}</td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap', color: ticket.ersteAktivitaet ? 'inherit' : 'var(--color-text-secondary)' }}>
                      {ticket.ersteAktivitaet ? fmtDateTime(ticket.ersteAktivitaet) : '–'}
                    </td>
                    <td className="td-subject" title={ticket.subject}>{ticket.subject}</td>
                    {!hideProdukt && <td>{ticket.produkt || '–'}</td>}
                    <td>
                      <span className={`kanal-badge kanal--${ticket.kanal}`}>
                        {KANAL_LABELS[ticket.kanal] || ticket.kanal}
                      </span>
                    </td>
                    <td>
                      {/* Status-Badge: bei solved/closed-Tickets die NICHT vom Agent
                          selbst geloest wurden, grauer Stil (extern geloest). */}
                      <span className={`status-badge ${
                        (ticket.status === 'solved' || ticket.status === 'closed') &&
                        ticket.solvedByAgent === false
                          ? 'status--solved-extern'
                          : (STATUS_COLORS[ticket.status] || '')
                      }`}>
                        {STATUS_LABELS[ticket.status] || ticket.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{ticket.gruppeName || '–'}</td>
                    <td>{ticket.bearbeitungFormatiert || '–'}</td>
                    <td style={{ fontSize: 11, color: '#185FA5', fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {expanded === ticket.id ? '▲ Schliessen' : '▼ Verlauf'}
                    </td>
                  </tr>
                  {expanded === ticket.id && (
                    <tr><td colSpan={hideProdukt ? 9 : 10} style={{ padding: 0 }}>
                      <TicketTimeline ticket={ticket} auditData={auditCache[ticket.id]} loading={!!auditLoading[ticket.id]} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {visibleCount < filtered.length && (
            <div className="load-more-row">
              <button
                type="button"
                className="load-more-btn"
                onClick={() => setVisibleCount(c => Math.min(c + PAGE_SIZE, filtered.length))}
              >
                Weitere {Math.min(PAGE_SIZE, filtered.length - visibleCount)} laden
                <span className="load-more-hint">({visibleCount} / {filtered.length})</span>
              </button>
              {filtered.length - visibleCount > PAGE_SIZE && (
                <button
                  type="button"
                  className="load-more-btn load-more-btn--all"
                  onClick={() => setVisibleCount(filtered.length)}
                >
                  Alle {filtered.length} laden
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
